import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { GroupCompletionRate, GroupsCompletionRateResponse } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-groups-completion-rate',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './ui-groups-completion-rate.component.html'
})
export class UiGroupsCompletionRateComponent implements OnInit, OnDestroy {
  public groupsCompletionRate: GroupCompletionRate[] = [];
  public loading = false;
  public error = false;
  public chartOptions: any;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {
    this.initializeChartOptions();
  }

  ngOnInit(): void {
    this.loadGroupsCompletionRate();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa as opções do gráfico com valores padrão
   */
  private initializeChartOptions(): void {
    this.chartOptions = {
      series: [
        {
          name: '',
          data: [],
        },
      ],
      colors: ['#845adf'],
      chart: {
        height: 335,
        type: 'bar',
        dropShadow: {
          enabled: true,
          color: '#000',
          top: 18,
          left: 7,
          blur: 10,
          opacity: 0.2,
        },
        toolbar: {
          show: false,
        },
        events: {
          mounted: (chart: any) => {
            chart.windowResizeHandler();
          },
        },
      },
      dataLabels: {
        enabled: true,
      },
      stroke: {
        curve: 'smooth',
      },
      title: {
        text: '',
      },
      grid: {
        borderColor: '#e7e7e7',
        row: {
          colors: ['#f3f3f3', 'transparent'],
          opacity: 0.5,
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
        },
      },
      xaxis: {
        categories: [],
        title: {
          text: '',
          style: {
            color: '#8c9097',
            fontSize: '13px',
            fontWeight: 'bold',
          },
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-xaxis-label',
          },
        },
      },
      yaxis: {
        title: {
          text: '',
        },
        labels: {
          show: true,
          style: {
            colors: '#8c9097',
            fontSize: '11px',
            fontWeight: 600,
            cssClass: 'apexcharts-yaxis-label',
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + '%';
          },
        },
      },
    };
  }

  public loadGroupsCompletionRate(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar dados dos grupos.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getGroupsCompletionRate()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.groupsCompletionRate = response.data;
            this.updateChartData(response.data);
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar dados dos grupos:', error);
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Atualiza os dados do gráfico com base nos grupos de conclusão recebidos
   * @param groupsCompletionRate Lista de grupos de conclusão da API
   */
  private updateChartData(groupsCompletionRate: GroupCompletionRate[]): void {
    const data = groupsCompletionRate.map(group => group.completion_percentage);
    const categories = groupsCompletionRate.map(group => group.name);
    const colors = groupsCompletionRate.map(group => this.getGroupColor(group.color));

    this.chartOptions = {
      ...this.chartOptions,
      series: [
        {
          name: '',
          data: data,
        },
      ],
      colors: colors,
      xaxis: {
        ...this.chartOptions.xaxis,
        categories: categories,
      },
    };
  }

  /**
   * Converte as cores do Tailwind para códigos hexadecimais
   * @param tailwindColor Classe de cor do Tailwind (ex: 'bg-blue-500')
   * @returns Código hexadecimal da cor
   */
  private getGroupColor(tailwindColor: string): string {
    const colorMap: { [key: string]: string } = {
      'bg-blue-500': '#3b82f6',
      'bg-green-500': '#10b981',
      'bg-yellow-500': '#f59e0b',
      'bg-red-500': '#ef4444',
      'bg-purple-500': '#8b5cf6',
      'bg-pink-500': '#ec4899',
      'bg-indigo-500': '#6366f1',
      'bg-teal-500': '#14b8a6',
      'bg-orange-500': '#f97316',
      'bg-cyan-500': '#06b6d4',
    };

    return colorMap[tailwindColor] || '#6b7280';
  }

  /**
   * Trata erros específicos da API
   * @param error Erro recebido da API
   */
  private handleApiError(error: any): void {
    this.error = true;
    
    // Log específico baseado no tipo de erro
    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    } else {
      console.error('Erro desconhecido ao carregar dados dos grupos:', error);
    }
  }
}
